import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import RequestHandler from "../../Functions/RequestHandler.js";

const bg = require("../../Assets/logo.png");

export default function Dashboard() {
	interface Service {
		servicePrice: any;
		id: number;
		serviceName: string;
		serviceURL: string;
		serviceURLS: [string];
		serviceImg: string;
		serviceDescription: string;
	}

	const [services, setServices] = useState<Service[]>([]);
	const [showServiceModal, setShowServiceModal] = useState<Service | null>(
		null
	);
	const [showAllServicesModal, setShowAllServicesModal] = useState(false);
	const [showAboutModal, setShowAboutModal] = useState(false);
	const [showContactModal, setShowContactModal] = useState(false);

	const getAllService = async () => {
		try {
			const data = await RequestHandler.handleRequest(
				"post",
				"service/get_services"
			);

			if (data.success) {
				setServices(data.services);
			} else {
				toast.error(
					data.message ||
						"Reloading services failed, please check your credentials."
				);
			}
		} catch (error) {
			toast.error(`An error occurred. ${error}`);
		}
	};

	useEffect(() => {
		getAllService();
	}, []);

	useEffect(() => {
		localStorage.clear();
		const message = sessionStorage.getItem("error-message");
		if (message) toast.error(message);
	}, []);

	return (
		<div className="w-[100vw] min-h-screen overflow-x-hidden">
			<ToastContainer />

			{/* Navigation Bar */}
			<nav className="flex justify-between items-center p-2 bg-green-800 text-white w-full mt-4 px-32">
				<div className="text-4xl font-bold">EFFICIO</div>
				<ul className="flex space-x-16 justify-center items-center">
					<li
						className="text-xl cursor-pointer"
						onClick={() => {
							setShowAllServicesModal(true);
						}}
					>
						Services
					</li>
					<li
						className="text-xl cursor-pointer"
						onClick={() => setShowAboutModal(true)}
					>
						About Us
					</li>
					<li
						className="text-xl bg-green-600 px-6 py-3 rounded-lg cursor-pointer"
						onClick={() => setShowContactModal(true)}
					>
						Contact Us
					</li>
				</ul>
			</nav>

			{/* Company Image */}
			<div className="flex justify-center items-center mt-4 shadow-lg">
				<img
					src={bg}
					alt="Company Image"
					className="w-full px-32 h-[50vh] object-cover"
				/>
			</div>

			{/* Services Grid */}
			<div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 px-32 p-8 mt-1">
				{/* Text Section */}
				<div className="col-span-1 lg:col-span-1 p-6 rounded-lg text-center">
					<h2 className="text-3xl font-bold text-left">
						Simplifying Permits & Taxes
					</h2>
					<p className="mt-4 text-gray-600 text-left">
						We help streamline the process of acquiring permits and
						handling taxes efficiently, ensuring a hassle-free
						experience for you.
					</p>
					<NavLink to="login">
						<div className="w-full font-bold text-3x1 mt-6 bg-green-600 text-white px-6 py-2 rounded-full hover:bg-green-700 transition duration-200">
							GET STARTED {">"}
						</div>
					</NavLink>
				</div>

				{/* First 3 Services */}
				{services.slice(0, 3).map((service) => (
					<img
						key={service.id}
						src={service.serviceImg}
						alt={service.serviceName}
						onClick={() => setShowServiceModal(service)}
						className="bg-white p-6 rounded-lg shadow-xl text-center border-r-8 border-b-4 border-green-600 h-72 w-full object-cover transform transition-transform duration-200 hover:scale-105 cursor-pointer"
					/>
				))}
			</div>

			{/* Single Service Modal */}
			{showServiceModal && (
				<div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center p-4">
					<div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative">
						<button
							className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
							onClick={() => setShowServiceModal(null)}
						>
							✕
						</button>
						<img
							src={showServiceModal.serviceImg}
							alt={showServiceModal.serviceName}
							className="w-full h-60 object-cover rounded mb-4"
						/>
						<h2 className="text-2xl font-bold text-green-700">
							{showServiceModal.serviceName}
						</h2>
						<p className="mt-2 text-gray-600">
							{showServiceModal.serviceDescription}
						</p>
					</div>
				</div>
			)}

			{showAllServicesModal && (
				<div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-start overflow-y-auto p-4">
					<div className="bg-white rounded-lg shadow-xl w-full max-w-4xl p-6 mt-10 max-h-[90vh] overflow-y-auto">
						<div className="flex justify-between items-center mb-4">
							<h2 className="text-2xl font-bold text-green-700">
								All Services
							</h2>
							<button
								className="text-gray-500 hover:text-gray-700"
								onClick={() => setShowAllServicesModal(false)}
							>
								✕
							</button>
						</div>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							{services.map((service) => (
								<div
									key={service.id}
									className="border border-gray-200 rounded-lg p-4 shadow hover:shadow-md transition"
								>
									<img
										src={service.serviceImg}
										alt={service.serviceName}
										className="w-full h-48 object-cover rounded mb-3"
									/>
									<h3 className="text-xl font-semibold text-green-800">
										{service.serviceName}
									</h3>
									<p className="text-gray-600 mt-2">
										{service.serviceDescription}
									</p>
								</div>
							))}
						</div>
					</div>
				</div>
			)}

			{/* About Us Modal */}
			{showAboutModal && (
				<div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center p-4">
					<div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative">
						<button
							className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
							onClick={() => setShowAboutModal(false)}
						>
							✕
						</button>
						<h2 className="text-2xl font-bold mb-4">About Us</h2>
						<p className="text-gray-700">
							We are a company dedicated to simplifying your
							permit and tax processes. Our team ensures a smooth
							experience, saving you time and resources.
						</p>
					</div>
				</div>
			)}

			{/* Contact Us Modal */}
			{showContactModal && (
				<div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center p-4">
					<div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative">
						<button
							className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
							onClick={() => setShowContactModal(false)}
						>
							✕
						</button>
						<h2 className="text-2xl font-bold mb-4">Contact Us</h2>
						<p className="text-gray-700">
							Email: support@efficio.com
							<br />
							Phone: +123-456-7890
							<br />
							Address: 123 Main Street, Business City
						</p>
					</div>
				</div>
			)}
		</div>
	);
}
